import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, LayoutDashboard, Users, CalendarDays, FileText, ClipboardList, Menu, X, MapPin, BarChart3, Send, Settings, ShieldAlert, Mail, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const LOGOS = {
  rrdc: "https://customer-assets.emergentagent.com/job_field-rental/artifacts/8kaelsop_Untitled%20design.png",
  riverview: "https://customer-assets.emergentagent.com/job_field-rental/artifacts/ov6p5t0e_Riverview%20Athletics%20Logo.png",
  turf_house: "https://customer-assets.emergentagent.com/job_field-rental/artifacts/nltdiuae_turf%20blog.png",
};

const NAV_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/leads", label: "Leads", icon: ClipboardList },
  { path: "/calendar", label: "Calendar", icon: CalendarDays },
  { path: "/contract-templates", label: "Contracts", icon: FileText, roles: ["super_admin", "company_admin"] },
  { path: "/signed-waivers", label: "Signed Waivers", icon: ClipboardCheck },
  { path: "/venues", label: "Venues", icon: MapPin, roles: ["super_admin", "company_admin"] },
  { path: "/employees", label: "Users", icon: Users, roles: ["super_admin", "company_admin"] },
  { path: "/blast", label: "Mass Blast", icon: Send, roles: ["super_admin", "company_admin"] },
  { path: "/email", label: "Email Marketing", icon: Mail, roles: ["super_admin", "company_admin"] },
  { path: "/reports", label: "Reports", icon: BarChart3 },
  { path: "/settings/calendar", label: "Google Calendar", icon: Settings, roles: ["super_admin", "company_admin"] },
  { path: "/admin/testing", label: "Testing Tools", icon: ShieldAlert, roles: ["super_admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const brandLogo = user?.brand === "riverview" ? LOGOS.riverview : LOGOS.rrdc;
  const brandName = user?.brand === "riverview" ? "Riverview Athletics" : user?.role === "super_admin" ? "Turf Booking" : "Rolling River";

  const filteredNav = NAV_ITEMS.filter(
    (item) => !item.roles || item.roles.includes(user?.role)
  );

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-border flex flex-col transition-transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="p-4 border-b border-border flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 overflow-hidden bg-transparent">
            <img src={brandLogo} alt="Logo" className="w-8 h-8 object-contain" />
          </div>
          <span className="font-outfit font-semibold text-foreground truncate">{brandName}</span>
          <button className="lg:hidden ml-auto text-muted-foreground" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {filteredNav.map((item) => {
            const Icon = item.icon;
            const active = location.pathname.startsWith(item.path);
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                <Icon size={18} />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 text-xs text-muted-foreground">
            {user?.first_name} {user?.last_name}
            <span className="block text-xs opacity-60 capitalize">{user?.role?.replace("_", " ")}</span>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-card/80 backdrop-blur-xl flex items-center px-4 gap-3 sticky top-0 z-30">
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden text-muted-foreground"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={20} />
          </button>
          <div className="flex-1" />
          <Button
            data-testid="logout-btn"
            variant="ghost"
            size="icon"
            onClick={handleLogout}
          >
            <LogOut size={18} />
          </Button>
        </header>
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
